import { FocusEvent, MouseEvent, KeyboardEvent } from 'react';

interface ExternalTriggers {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export const useMixedPlaceholder = (
  enabled: boolean = false,
  externalTriggers: ExternalTriggers = {}
) => {
  const { onFocus, onClick, onBlur, onKeyDown } = externalTriggers;

  if (!enabled) {
    return { onFocus, onClick, onBlur, onKeyDown };
  }

  const handleKeyDownCapture = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const hasText = target.value.length > 0;
    const isTextSelected =
      target.selectionStart === 0 &&
      target.selectionEnd === target.value.length;

    if (hasText && isTextSelected) {
      const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (arrowKeys.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        return;
      }

      if (e.key.length === 1) {
        e.preventDefault();

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value'
        )?.set;
        nativeInputValueSetter?.call(target, e.key);
        target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
    if (onFocus) onFocus(e);
  };

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    e.currentTarget.select();
    if (onClick) onClick(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(e);
  };

  return {
    onFocus: handleFocus,
    onClick: handleClick,
    onBlur: handleBlur,
    onKeyDownCapture: handleKeyDownCapture,
  };
};
